import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {OfwcaWithUser360Profiles, User} from "../../../../shared/interface/user";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Ofwca} from "../../../../shared/interface/ofwca";
import {FacilityDobryAgent, ImageResponsive} from "../../../../shared/interface/facility-dobry-agent";
import {environment} from "../../../../../environments/environment";
import {catchError, finalize, first, map, switchMap, takeUntil} from "rxjs/operators";
import {MatSnackBar} from "@angular/material/snack-bar";
import {DobryAgentFacilityService} from "../../../../shared/services/dobry-agent-facility.service";
import {UserService} from "../../../../shared/services/user.service";
import {combineLatest, forkJoin, Observable, of, ReplaySubject} from "rxjs";
import {EmailAddress} from "../../../../shared/interface/email-address";

@Component({
  selector: 'app-dobry-agent-agent-edit',
  templateUrl: './dobry-agent-agent-edit.component.html',
  styleUrls: ['./dobry-agent-agent-edit.component.scss']
})
export class DobryAgentAgentEditComponent implements OnInit,OnDestroy {
  @Input() ofwcas: Observable<Ofwca[]>;
  @Input() facilityDobryAgent: Observable<FacilityDobryAgent>;

  @Output() closeModification = new EventEmitter<any>();
  processingRequest = false;
  ofwcasDataSource: MatTableDataSource<OfwcaWithUser360Profiles> = new MatTableDataSource<OfwcaWithUser360Profiles>([]);
  agentForm: FormGroup;
  facilityDobryAgentObject?: FacilityDobryAgent = null;
  dobryAgentBaseUrl = environment.dobryAgentUrl;
  uploadingAgentImage: Record<string, boolean> = {};
  private readonly pendingAgentImages = new Map<string, File>();
  private readonly agentImagePreviews = new Map<string, string>();
  private readonly deletedAgentImages = new Set<string>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  loading = true;
  readonly maxAgentImageSize = 3 * 1024 * 1024;

  displayedColumns: string[] = [
    'fullName',
    'status',
    'portalDa',
    'phone',
    'email',
    'blog',
    'edit',
    'avatar',
  ];
  constructor(
    private snackBar: MatSnackBar,
    private facilityDobryAgentService: DobryAgentFacilityService,
    private userService: UserService,
    private fb: FormBuilder,
  ) {
    this.agentForm = this.fb.group({
      ofwcaPermissions: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    combineLatest([
      this.facilityDobryAgent,
      this.ofwcas,
    ])
      .pipe(
        switchMap(([facilityDobryAgent, ofwcas]: [FacilityDobryAgent, Ofwca[]]) => {
          this.facilityDobryAgentObject = facilityDobryAgent;

          return this.loadUsers360Profiles(ofwcas);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((ofwcas: OfwcaWithUser360Profiles[]) => {
        this.ofwcasDataSource.data = ofwcas;
        this.patchOfwcaPermissions(ofwcas);
        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  cancel(): void {
    this.closeModification.emit();
  }

  save(): void {
    if (this.agentForm.invalid) {
      this.agentForm.markAllAsTouched();
      this.snackBar.open('Popraw dane doradców przed zapisem', '', { duration: 2500 });
      return;
    }

    const asf24AddressUuid = this.facilityDobryAgentObject?.asf24AddressUuid;

    if (!asf24AddressUuid) {
      this.snackBar.open('Brak identyfikatora placówki ASF24', '', { duration: 2500 });
      return;
    }

    const rawValue = this.agentForm.getRawValue();
    const agents = this.prepareAgentsPayload(rawValue.ofwcaPermissions ?? []);

    this.processingRequest = true;

    this.facilityDobryAgentService.updateAgents(
      asf24AddressUuid,
      agents
    )
      .pipe(
        first(),
        switchMap(() => this.uploadPendingAgentImages(asf24AddressUuid)),
        switchMap(() => this.updateDobryAgentUsersPermissions(rawValue.ofwcaPermissions ?? []))
      )
      .subscribe({
        next: () => {
          this.processingRequest = false;
          this.snackBar.open('Pomyślnie zaktualizowano doradców placówki', '', { duration: 2000 });
          this.closeModification.emit();
        },
        error: (error: any) => {
          this.processingRequest = false;

          const backendMessage = this.applyBackendAgentErrors(error);

          const message =
            backendMessage
            ?? error?.error?.error?.text
            ?? error?.error?.message
            ?? 'Podczas zapisu doradców wystąpił błąd';

          this.snackBar.open(message, '', { duration: 4000 });
        },
      });
  }

  private applyBackendAgentErrors(error: any): string | null {
    const responseError = error?.error?.error;
    const agentsErrors = responseError?.list?.agents;

    if (agentsErrors && typeof agentsErrors === 'object') {
      Object.keys(agentsErrors).forEach((indexKey: string) => {
        const index = Number(indexKey);

        if (
          !Number.isInteger(index)
          || index < 0
          || index >= this.ofwcaPermissionsArray.length
        ) {
          return;
        }

        const group = this.getOfwcaPermissionGroup(index);
        const fieldErrors = agentsErrors[indexKey] ?? {};

        Object.keys(fieldErrors).forEach((fieldName: string) => {
          const control = group.get(fieldName);

          if (!control) {
            return;
          }

          const messages = fieldErrors[fieldName];
          const message = Array.isArray(messages)
            ? messages[0]
            : messages;

          if (!message) {
            return;
          }

          control.setErrors({
            ...(control.errors ?? {}),
            backendError: String(message),
          });
          control.markAsTouched();
        });
      });

      return responseError?.text ?? null;
    }

    // Fallback dla starej odpowiedzi 500 z Doctrine.
    const detail = String(
      error?.error?.detail
      ?? error?.error?.message
      ?? ''
    );

    const duplicateMatch = detail.match(
      /Duplicate entry '([^']+)' for key 'agent\.[^']+'/i
    );

    if (!duplicateMatch?.[1]) {
      return null;
    }

    const duplicateEmail = duplicateMatch[1].trim().toLowerCase();

    const index = this.ofwcaPermissionsArray.controls.findIndex((control) => {
      const email = String(control.get('email')?.value ?? '')
        .trim()
        .toLowerCase();

      return email === duplicateEmail;
    });

    const message =
      `Adres e-mail „${duplicateMatch[1]}” jest już przypisany do innego doradcy.`;

    if (index >= 0) {
      const emailControl = this.getOfwcaPermissionGroup(index).get('email');

      emailControl?.setErrors({
        ...(emailControl.errors ?? {}),
        backendError: message,
      });
      emailControl?.markAsTouched();
    }

    return message;
  }

  getOfwcaPermissionControl(index: number, controlName: string): FormControl {
    return this.getOfwcaPermissionGroup(index).get(controlName) as FormControl;
  }

  getOfwcaPermissionGroup(index: number): FormGroup {
    return this.ofwcaPermissionsArray.at(index) as FormGroup;
  }

  get ofwcaPermissionsArray(): FormArray {
    return this.agentForm.get('ofwcaPermissions') as FormArray;
  }

  hasUser360Account(element: any): boolean {
    return !!element?.user360Profiles?.length;
  }


  getAgentAvatarUrl(element: OfwcaWithUser360Profiles): string {
    if (element?.uuid && this.deletedAgentImages.has(element.uuid)) {
      return '';
    }

    const previewUrl = element?.uuid
      ? this.agentImagePreviews.get(element.uuid)
      : null;

    if (previewUrl) {
      return previewUrl;
    }

    const coordinator = this.getDobryAgentCoordinator(element);

    const imageUrl =
      this.getImageVariantUrl(coordinator?.featuredImage, 'medium')
      ?? coordinator?.featuredImage?.src
      ?? this.getImageVariantUrl((element as any)?.featuredImage, 'medium')
      ?? (element as any)?.featuredImage?.src
      ?? '';

    if (!imageUrl) {
      return '';
    }

    if (
      imageUrl.startsWith('http://') ||
      imageUrl.startsWith('https://') ||
      imageUrl.startsWith('data:image')
    ) {
      return imageUrl;
    }

    return `${this.dobryAgentBaseUrl.replace(/\/$/, '')}/${imageUrl.replace(/^\//, '')}`;
  }


  getDobryAgentCoordinator(ofwca: Ofwca): any | null {
    const coordinators = this.facilityDobryAgentObject?.coordinators ?? [];
    const ofwcaUuid = ofwca?.uuid;

    if (!ofwcaUuid) {
      return null;
    }

    return coordinators.find((coordinator: any) => {
      return coordinator.ofwca24Id === ofwcaUuid;
    }) ?? null;
  }

  private getImageVariantUrl(
    image: ImageResponsive | null | undefined,
    preferredSize: 'small' | 'medium' | 'large' | 'original' = 'medium'
  ): string | null {
    if (!image) {
      return null;
    }

    const preferredVariant = image.variants?.find(
      (variant) => variant.size === preferredSize
    );

    if (preferredVariant?.url) {
      return preferredVariant.url;
    }

    return image.src ?? null;
  }


  onAgentImageSelected(
    fileList: FileList | null,
    element: OfwcaWithUser360Profiles,
    index: number
  ): void {
    const file = fileList?.item(0);

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.snackBar.open('Wybrany plik nie jest obrazem', '', {
        duration: 2000,
      });

      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      this.snackBar.open('Dozwolone formaty to JPG, PNG lub WEBP', '', {
        duration: 2500,
      });

      return;
    }


    if (file.size > this.maxAgentImageSize) {
      this.snackBar.open('Avatar może mieć maksymalnie 3 MB', '', {
        duration: 2500,
      });

      return;
    }

    const visibleOnDobryAgent = !!this.getOfwcaPermissionControl(
      index,
      'visibleOnDobryAgent'
    )?.value;

    if (!visibleOnDobryAgent) {
      this.snackBar.open(
        'Najpierw zaznacz doradcę jako widocznego na portalu DA.',
        '',
        { duration: 2500 }
      );
      return;
    }

    this.deletedAgentImages.delete(element.uuid);

    const agentId = this.resolveDobryAgentAgentId(element);

    if (!agentId) {
      this.pendingAgentImages.set(element.uuid, file);
      this.previewAgentImage(file, element, index);

      this.snackBar.open(
        'Zdjęcie zostało wybrane i zostanie wgrane podczas zapisu doradcy.',
        '',
        { duration: 3000 }
      );
      return;
    }

    this.uploadingAgentImage[agentId] = true;

    this.facilityDobryAgentService.uploadAgentImage(agentId, file)
      .pipe(
        first(),
        finalize(() => {
          this.uploadingAgentImage[agentId] = false;
        })
      )
      .subscribe({
        next: () => {
          this.previewAgentImage(file, element, index);

          this.snackBar.open('Avatar doradcy został zaktualizowany', '', {
            duration: 2000,
          });
        },
        error: () => {
          this.snackBar.open('Nie udało się wgrać avatara doradcy', '', {
            duration: 2500,
          });
        },
      });
  }

  deleteAgentImage(
    element: OfwcaWithUser360Profiles
  ): void {
    const ofwcaUuid = element?.uuid;

    if (!ofwcaUuid) {
      return;
    }

    const hasPendingImage = this.pendingAgentImages.has(ofwcaUuid);
    const agentId = this.resolveDobryAgentAgentId(element);

    if (!agentId) {
      if (!hasPendingImage) {
        return;
      }

      this.pendingAgentImages.delete(ofwcaUuid);
      this.agentImagePreviews.delete(ofwcaUuid);

      this.snackBar.open('Wybrane zdjęcie zostało usunięte', '', {
        duration: 2000,
      });

      return;
    }

    this.uploadingAgentImage[agentId] = true;

    this.facilityDobryAgentService.deleteAgentImage(agentId)
      .pipe(
        first(),
        finalize(() => {
          this.uploadingAgentImage[agentId] = false;
        })
      )
      .subscribe({
        next: () => {
          this.pendingAgentImages.delete(ofwcaUuid);
          this.agentImagePreviews.delete(ofwcaUuid);
          this.deletedAgentImages.add(ofwcaUuid);

          const coordinator = this.getDobryAgentCoordinator(element);

          if (coordinator) {
            coordinator.featuredImage = null;
          }

          this.snackBar.open('Avatar doradcy został usunięty', '', {
            duration: 2000,
          });
        },
        error: (error: any) => {
          const message =
            error?.error?.error?.text
            ?? error?.error?.message
            ?? 'Nie udało się usunąć avatara doradcy';

          this.snackBar.open(message, '', {
            duration: 3500,
          });
        },
      });
  }

  resolveDobryAgentAgentId(ofwca: Ofwca): string | null {
    const coordinator = this.getDobryAgentCoordinator(ofwca);

    return coordinator?.uuid ?? null;
  }

  isAgentImageUploading(element: Ofwca): boolean {
    const imageKey = this.resolveDobryAgentAgentId(element) ?? element?.uuid;

    return !!imageKey && !!this.uploadingAgentImage[imageKey];
  }
  private previewAgentImage(
    file: File,
    element: OfwcaWithUser360Profiles,
    _index: number
  ): void {
    if (!element?.uuid) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const previewUrl = reader.result?.toString() ?? '';

      if (!previewUrl) {
        return;
      }

      this.agentImagePreviews.set(element.uuid, previewUrl);
    };

    reader.readAsDataURL(file);
  }
  private loadUsers360Profiles(ofwcas: Ofwca[]): Observable<OfwcaWithUser360Profiles[]> {
    const userIds = Array.from(new Set(
      (ofwcas ?? [])
        .reduce((ids: string[], ofwca: Ofwca) => {
          (ofwca.user360 ?? []).forEach((user) => {
            if (user?.uuid) {
              ids.push(user.uuid);
            }
          });

          return ids;
        }, [])
    ));

    if (!userIds.length) {
      return of(this.mapOfwcasWithUsers(ofwcas, []));
    }

    return this.userService.getDobryAgentUsers(userIds).pipe(
      map((response) => this.mapOfwcasWithUsers(ofwcas, response?.items ?? [])),
      catchError(() => of(this.mapOfwcasWithUsers(ofwcas, [])))
    );
  }

  private mapOfwcasWithUsers(
    ofwcas: Ofwca[],
    users: User[]
  ): OfwcaWithUser360Profiles[] {
    const usersByUuid = new Map<string, User>();

    (users ?? []).forEach((user: User) => {
      if (user?.uuid) {
        usersByUuid.set(user.uuid, user);
      }
    });

    return (ofwcas ?? []).map((ofwca: Ofwca) => ({
      ...ofwca,
      user360Profiles: (ofwca.user360 ?? [])
        .map((user) => user?.uuid ? usersByUuid.get(user.uuid) : null)
        .filter((user): user is User => !!user),
    } as OfwcaWithUser360Profiles));
  }

  private patchOfwcaPermissions(ofwcas: OfwcaWithUser360Profiles[]): void {
    this.ofwcaPermissionsArray.clear();

    (ofwcas ?? []).forEach((ofwca: OfwcaWithUser360Profiles) => {
      this.ofwcaPermissionsArray.push(this.buildOfwcaPermissionGroup(ofwca));
    });
  }

  private buildOfwcaPermissionGroup(ofwca: OfwcaWithUser360Profiles): FormGroup {
    const coordinator = this.getDobryAgentCoordinator(ofwca);
    const hasUser360Account = this.hasUser360Account(ofwca);

    const isVisibleOnDobryAgent = !!coordinator && coordinator.active === true;

    const group = this.fb.group({
      uuid: [ofwca.uuid],
      dobryAgentAgentUuid: [coordinator?.uuid ?? null],
      firstName: [ofwca.firstName ?? coordinator?.firstName ?? ''],
      lastName: [ofwca.lastName ?? coordinator?.lastName ?? ''],

      email: [{
        value:
          coordinator?.email
          ?? this.getOfwcaEmail(ofwca),
        disabled: !isVisibleOnDobryAgent,
      }],

      phone: [{
        value:
          coordinator?.phone
          ?? ofwca.phone
          ?? '',
        disabled: !isVisibleOnDobryAgent,
      }],

      user24Id: [
        this.resolveOfwcaUser24Id(ofwca)
        ?? coordinator?.user24Id
        ?? null
      ],

      visibleOnDobryAgent: [isVisibleOnDobryAgent],

      blogAccess: [
        {
          value: this.hasDobryAgentBlogAccess(ofwca),
          disabled: !hasUser360Account,
        },
      ],

      businessCardEditAccess: [
        {
          value: this.hasDobryAgentBusinessCardEditAccess(ofwca),
          disabled: !hasUser360Account,
        },
      ],
    });

    this.updateAgentContactValidators(group, isVisibleOnDobryAgent);

    return group;
  }

  onVisibleOnDobryAgentChange(index: number): void {
    const group = this.getOfwcaPermissionGroup(index);
    const isVisible = !!group.get('visibleOnDobryAgent')?.value;

    this.updateAgentContactValidators(group, isVisible);

    if (isVisible) {
      group.get('phone')?.markAsTouched();
      group.get('email')?.markAsTouched();
      return;
    }

    this.clearPendingAgentImage(index);
  }

  private updateAgentContactValidators(
    group: FormGroup,
    required: boolean
  ): void {
    const phoneControl = group.get('phone');
    const emailControl = group.get('email');

    if (!phoneControl || !emailControl) {
      return;
    }

    if (required) {
      phoneControl.enable({emitEvent: false});
      emailControl.enable({emitEvent: false});

      phoneControl.setValidators([
        Validators.required,
        Validators.pattern(/^(?:\+?48)?(?:\s?\d{3}){3}$/),
      ]);

      emailControl.setValidators([
        Validators.required,
        Validators.email,
      ]);
    } else {
      phoneControl.clearValidators();
      emailControl.clearValidators();

      phoneControl.disable({emitEvent: false});
      emailControl.disable({emitEvent: false});
    }

    phoneControl.updateValueAndValidity({emitEvent: false});
    emailControl.updateValueAndValidity({emitEvent: false});
  }


  private clearPendingAgentImage(index: number): void {
    const group = this.getOfwcaPermissionGroup(index);
    const ofwcaUuid = group.get('uuid')?.value;

    if (!ofwcaUuid) {
      return;
    }

    this.pendingAgentImages.delete(ofwcaUuid);
    this.agentImagePreviews.delete(ofwcaUuid);
  }

  private uploadPendingAgentImages(asf24AddressUuid: string): Observable<any[]> {
    if (!this.pendingAgentImages.size) {
      return of([]);
    }

    return this.facilityDobryAgentService.get(asf24AddressUuid).pipe(
      first(),
      switchMap((response: any) => {
        const facility = response?.data ?? response;
        const coordinators = facility?.coordinators ?? [];
        const requests: Observable<any>[] = [];

        this.pendingAgentImages.forEach((file: File, ofwca24Id: string) => {
          const agent = coordinators.find((coordinator: any) => {
            return coordinator.ofwca24Id === ofwca24Id;
          });

          if (!agent?.uuid) {
            throw new Error(`Nie znaleziono utworzonego doradcy DA dla OFWCA ${ofwca24Id}`);
          }

          this.uploadingAgentImage[agent.uuid] = true;

          requests.push(
            this.facilityDobryAgentService.uploadAgentImage(agent.uuid, file).pipe(
              first(),
              map((result: any) => {
                this.pendingAgentImages.delete(ofwca24Id);
                return result;
              }),
              finalize(() => {
                this.uploadingAgentImage[agent.uuid] = false;
              })
            )
          );
        });

        return requests.length ? forkJoin(requests) : of([]);
      })
    );
  }

  private resolveOfwcaUser24Id(ofwca: any): string | null {
    const user360 = ofwca.user360 ?? [];

    if (!Array.isArray(user360) || user360.length === 0) {
      return null;
    }

    return user360[0]?.uuid ?? null;
  }

  hasDobryAgentBlogAccess(element: any): boolean {
    return this.getDobryAgentPermission(
      element,
      'blog'
    ) === 'active';
  }

  hasDobryAgentBusinessCardEditAccess(element: any): boolean {
    return this.getDobryAgentPermission(
      element,
      'business_card'
    ) === 'active';
  }

  hasDobryAgentApplicationAccess(element: any): boolean {
    return this.getDobryAgentPermission(element, 'application_access') === 'active';
  }
  getDobryAgentPermission(element: any, module: string): string {
    const user = this.getPrimaryUser360(element);

    if (!user) {
      return 'none';
    }

    const permissions = this.getEffectiveDobryAgentPermissionsFromUser(user);

    return permissions?.[module] || 'none';
  }
  private getPrimaryUser360(element: any): User | null {
    if (!element?.user360Profiles || element.user360Profiles.length === 0) {
      return null;
    }

    return element.user360Profiles[0];
  }

  private getEffectiveDobryAgentPermissionsFromUser(user: User): any {
    if (!user) {
      return {};
    }

    const userPermissions = this.normalizeApplicationPermissions(user.applicationPermissions);
    const userDobryAgentPermissions = userPermissions?.dobryagent || {};

    let departmentDobryAgentPermissions: any = {};

    if (user.department?.applicationPermissions) {
      const departmentPermissions = this.normalizeDepartmentApplicationPermissions(
        user.department.applicationPermissions,
        'employee'
      );

      departmentDobryAgentPermissions = departmentPermissions?.dobryagent || {};
    }

    return {
      application_access:
        departmentDobryAgentPermissions?.application_access
        ?? userDobryAgentPermissions?.application_access
        ?? 'none',

      blog:
        userDobryAgentPermissions?.blog
        ?? 'none',

      business_card:
        userDobryAgentPermissions?.business_card
        ?? 'none',
    };
  }



  private getOfwcaEmail(ofwca: Ofwca): string {
    const emails = ofwca?.emails ?? [];
    const email = emails.find((item: EmailAddress) => item.main) ?? emails[0];

    return email?.address ?? email?.name ?? '';
  }

  private prepareAgentsPayload(ofwcaPermissions: any[]): any[] {
    return (ofwcaPermissions ?? [])
      .filter((item: any) => {
        return !!item.visibleOnDobryAgent || !!item.dobryAgentAgentUuid;
      })
      .map((item: any) => ({
        ofwca24Id: item.uuid,
        user24Id: item.user24Id ?? null,
        firstName: item.firstName ?? '',
        lastName: item.lastName ?? '',
        email: item.email ?? '',
        phone: item.phone ?? null,
        photo: item.photo ?? null,
        active: !!item.visibleOnDobryAgent,
        deleted: false,
        dobryAgentPermissions: {
          blog: item.blogAccess ? 'active' : 'none',
          business_card: item.businessCardEditAccess ? 'active' : 'none',
        },
      }));
  }

  private buildDobryAgentApplicationPermissionsPayload(
    user: User,
    blogAccess: boolean,
    businessCardEditAccess: boolean
  ): any {
    const currentPermissions = this.normalizeApplicationPermissions(user.applicationPermissions);

    return {
      ...currentPermissions,
      dobryagent: {
        ...(currentPermissions?.dobryagent ?? {}),
        blog: blogAccess ? 'active' : 'none',
        business_card: businessCardEditAccess ? 'active' : 'none',
      },
    };
  }

  private updateDobryAgentUsersPermissions(ofwcaPermissions: any[]): Observable<any[]> {
    const requests = (ofwcaPermissions ?? [])
      .map((item: any) => {
        const ofwca = this.ofwcasDataSource.data.find((dataItem: OfwcaWithUser360Profiles) => {
          return dataItem.uuid === item.uuid;
        });

        const user = this.getPrimaryUser360(ofwca);

        if (!user?.uuid) {
          return null;
        }

        const payload = this.buildDobryAgentApplicationPermissionsPayload(
          user,
          !!item.blogAccess,
          !!item.businessCardEditAccess
        );

        return this.userService.updateApplicationPermissions(user.uuid, payload);
      })
      .filter((request: Observable<any> | null): request is Observable<any> => !!request);

    return requests.length ? forkJoin(requests) : of([]);
  }

  private normalizeApplicationPermissions(applicationPermissions: any): any {
    const result = {};

    if (!applicationPermissions || !Array.isArray(applicationPermissions)) {
      return result;
    }

    applicationPermissions.forEach((item: any) => {
      if (item.applicationKey) {
        result[item.applicationKey] = item.permissions || {};
      }
    });

    return result;
  }

  private normalizeDepartmentApplicationPermissions(
    applicationPermissions: any,
    assignmentType: 'employee' | 'supervisor'
  ): any {
    const result = {};

    if (!applicationPermissions || !Array.isArray(applicationPermissions)) {
      return result;
    }

    applicationPermissions
      .filter((item: any) => item.assignmentType === assignmentType)
      .forEach((item: any) => {
        result[item.applicationKey] = item.permissions || {};
      });

    return result;
  }


}
